import React, { useEffect, useRef, useState } from "react";
import { Media, MontanaMediaRatios } from '@montana/shared/media';
import { Translate } from "@montana/shared/translate";
import { Tablist } from "@montana/shared/tablist";
import { useProduct } from "@montana/containers/product";
import { ITabProps } from "@montana/shared/tablist/tablist.component";
import { useTranslations } from "@montana/containers/translations";
import { Link } from "@montana/shared/link";
import { PdpSpecificationsTab, usePDPContext } from "../product-page.context";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { CUSTOMER_MODE } from "@montana/constants/customer-mode";
import { useSettings } from '@montana/containers/settings/settings.container';
import { useMediaQuery } from 'react-responsive';
import { Button } from "@montana/shared/button";
import { SvgIcon } from "@montana/shared/svg-icon";

export const SpecificationsSection = () => {

    const { productTitleH1 } = useProduct();
    const { activeTab, setActiveTab, scrollToTabUpdate } = usePDPContext();
    const blockRef = useRef<HTMLElement>(null);
    const translate = useTranslations();
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [firstRun, setFirstRun] = useState<boolean>(true);

    useEffect(() => {
        const topOffset = isMobile ? 100 : 0;

        if (blockRef?.current && !firstRun) {
            window.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: blockRef?.current?.offsetTop - topOffset
            })
        }
        setFirstRun(false);

    }, [scrollToTabUpdate]);

    const tabs: ITabProps[] = [
        {
            header: translate(t => t.productPage.productPageSpecifications, '[Specifications]'),
            fixedId: PdpSpecificationsTab.SPECIFICATION,
            content: <Specifications />,
        },
        {
            header: translate(t => t.productPage.productPageDescription, '[Description]'),
            fixedId: PdpSpecificationsTab.DESCRIPTION,
            content: <Description />,
        },
        {
            header: translate(t => t.productPage.productPageDownloadsTitle, '[Downloads]'),
            fixedId: PdpSpecificationsTab.RESOURCES,
            content: <Resources />,
        }
    ];

    return (
        <section className="specifications-section" ref={blockRef}>
            <h2 className="specifications-section__title">{productTitleH1} <Translate value="productPage.details" defaultValue="[details]"></Translate></h2>
            <Tablist tabs={tabs} onTabChanged={tabId => setActiveTab(tabId as PdpSpecificationsTab)} activeTabId={activeTab} />
        </section>
    );
};

const Specifications = () => {

    const { properties, lineDrawing, prices, designer, measurementRanges, baseModelMaterial1, baseModelMaterial2, baseModelMaterial3 } = useProduct();
    const { customerMode } = useSettings();
    /*const [colorOptions, setColorOptions] = useState<string | undefined>();
    useEffect(() => {
        const colorDimension = dimensions?.find(d => d.seoFriendlyDimensionName === 'colour');
        if (colorDimension?.options?.length) {
            setColorOptions(
                colorDimension.options
                    .filter(o => o.displayValue)
                    .map(o => o.displayValue!)
                    .join(', ')
            );
        }
    }, [dimensions]);*/

    return (
        <div className={`specifications-section__specifications ${lineDrawing ? 'specifications-section__specifications--has-image' : ''}`}>
            {!!lineDrawing?.url &&
                <div className="specifications-section__specifications-image">
                    <Media
                        image={lineDrawing}
                        placeholder={false}
                        animate={true}
                        mediaRatio={MontanaMediaRatios.Square}
                    />
                </div>
            }

            <div className="specifications-section__specifications-column">
                {customerMode === CUSTOMER_MODE.PROFESSIONAL && !!measurementRanges && (
                    <div className="specifications-section__specifications-group">
                        <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageMeasurements" defaultValue="[Measurements]" /></div>
                        {measurementRanges.map((measurementRange, index) => {
                            return (
                                <div key={`specifications-section-measurementRange-${index}`} className="specifications-section__specifications-value">{measurementRange}</div>
                            )
                        })}
                    </div>
                )}
                {(customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) && !!prices?.measurements && (
                    <div className="specifications-section__specifications-group">
                        <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageMeasurements" defaultValue="[Measurements]" /></div>
                        <div className="specifications-section__specifications-value">{prices.measurements}</div>
                    </div>
                )}

                {customerMode === CUSTOMER_MODE.PROFESSIONAL && 
                    (!!baseModelMaterial1 || !!baseModelMaterial2 || !!baseModelMaterial3) && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageMaterial" defaultValue="[Material]" /></div>
                            {!!baseModelMaterial1 && <div className="specifications-section__specifications-value">{baseModelMaterial1}</div>}
                            {!!baseModelMaterial2 && <div className="specifications-section__specifications-value">{baseModelMaterial2}</div>}
                            {!!baseModelMaterial3 && <div className="specifications-section__specifications-value">{baseModelMaterial3}</div>}
                        </div>
                    )
                }

                {(customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) && 
                    (!!prices?.materialOne || !!prices?.materialTwo || !!prices?.materialThree) && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageMaterial" defaultValue="[Material]" /></div>
                            {!!prices?.materialOne && <div className="specifications-section__specifications-value">{prices?.materialOne}</div>}
                            {!!prices?.materialTwo && <div className="specifications-section__specifications-value">{prices?.materialTwo}</div>}
                            {!!prices?.materialThree && <div className="specifications-section__specifications-value">{prices?.materialThree}</div>}
                        </div>
                    )
                }

                {/*{!!colorOptions?.length && (
                    <div className="specifications-section__specifications-group">
                        <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageColours" defaultValue="[Colours]" /></div>
                        <div className="specifications-section__specifications-value">{colorOptions}</div>
                    </div>
                )}*/}

                {!!designer?.designerName && (
                    <div className="specifications-section__specifications-group">
                        <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageDesigner" defaultValue="[Designer]" /></div>
                        <div className="specifications-section__specifications-value">{designer?.designerName}</div>
                    </div>
                )}

                {!!properties?.moreInfo && (
                    <div className="specifications-section__specifications-group">
                        <div className="specifications-section__specifications-heading"><Translate value="productPage.productPageMoreInfo" defaultValue="[More info]" /></div>
                        <div className="specifications-section__specifications-value">{properties.moreInfo}</div>
                        {!!properties.moreInfoLink && (
                            <Link target="_blank" to={properties.moreInfoLink}>
                                <Translate value="productPage.productPageMoreInfoLink" defaultValue="[Read more]" />
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const Description = () => {

    const { customerMode } = useSettings();
    const { productDescriptionLong, contractDescription } = useProduct();

    const description = customerMode === CUSTOMER_MODE.PROFESSIONAL ? contractDescription : productDescriptionLong;

    return description ? (
        <div className="specifications-section__description">
            {description}
        </div>
    ) : null;
};

const Resources = () => {

    const { properties, series, backroomLink } = useProduct();

    return (
        <div className="specifications-section__resources">
            {backroomLink && 
                <div className="specifications-section__resources-section">
                    <div className="specifications-section__resources-heading">
                        <Translate value="productPage.productPageDownloadsTitle" defaultValue="[Downloads]" />
                    </div>
                    <div className="specifications-section__resources-description">
                        <Translate value="productPage.productPageDownloadsDescription" defaultValue="[To find all downloads go to our Montana Backroom space. Here you will find everything from downloads to catalogues and videos.]" />
                    </div>
                    <Button className="specifications-section__resources-backroom" href={backroomLink}>
                        <Translate value="productPage.productPageGoToBackroom" defaultValue="[Go to Backroom]" />
                    </Button>
                </div>
            }
            <div className="specifications-section__resources-section specifications-section__resources-section-margin-top">
                <div className="specifications-section__resources-heading">
                    <Translate value="productPage.productPageQuickDownloadsTitle" defaultValue="[Quick downloads]" />
                </div>
                <div className="specifications-section__resources-description">
                    <Translate value="productPage.productPageQuickDownloadsDescription" defaultValue="[In this section you will find quick downloads to help you with your project.]" />
                </div>
                <div className="specifications-section__resources-links">
                    
                    {!!properties?.installationInstructionsLink && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={properties?.installationInstructionsLink}>
                                    <Translate value="productPage.productPageInstallationInstructions" defaultValue="[Installation Instructions]" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {!!properties?.installationsVideoLink && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={properties?.installationsVideoLink}>
                                    <Translate value="productPage.productPageInstallationVideo" defaultValue="[Installation Video]" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {!!properties?.fileLink3DModel && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={properties?.fileLink3DModel}>
                                    <Translate value="productPage.productPage3DFileLink" defaultValue="[3d Files]" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {!!series?.seriesCatalogLink && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={series?.seriesCatalogLink}>
                                    <Translate value="productPage.productPageCatalogue" defaultValue="[Catalogue]" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {!!properties?.maintenanceGuide && staticSettings.b2c && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={properties.maintenanceGuide}>
                                    <Translate value="productPage.productPageMaintenanceGuide" defaultValue="[Maintenance Guide]" />
                                </Link>
                            </div>
                        </div>
                    )}

                    {!!properties?.ecoLabelLink && (
                        <div className="specifications-section__specifications-group">
                            <div className="specifications-section__resources-link">
                                <SvgIcon iconName={'download'}/>
                                <Link withStroke={true} target="_blank" to={properties?.ecoLabelLink}>
                                    <Translate value="productPage.productPageEcoLabelLink" defaultValue="[Forbrugerinformation]" />
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
